<!--
  Bloco de diagnóstico da falha de integração de uma ocorrência na grade de
  Posição de Notas Fiscais (TMS-8396 fase 2 — ADR-0002, decisão D3).

  Componente de APRESENTAÇÃO: recebe props, emite eventos, não faz HTTP e não
  consulta permissão. A view continua dona do `Form`, do `ReturnApi`, do
  barramento de reload, da área de transferência e de `getPermissionFw`.

  Três propriedades sustentam o desenho e não devem ser "simplificadas":

  1. `resolverEstadoIntegracao(item)` devolvendo `null` faz o componente
     renderizar NADA. É o que permite este front subir antes de o backend
     publicar as 3 chaves novas: sem elas, a célula fica idêntica a hoje.

  2. O aberto/fechado dos detalhes é um booleano no `data()` desta instância.
     Inline na view viraria mais um array no molde de `visibleDetailRows`
     (`PosicaoNotaFiscalDetalhes.vue:3418`), com id derivado de `carocorrenId` e
     risco de colidir com o id do detail-row existente.

  3. Todo texto vindo da parceira (`motivoIntegracao`, `motivoIntegracaoLabel`)
     sai por `{{ }}`. NUNCA `v-html`, em lugar nenhum deste arquivo — é resposta
     crua de sistema externo, e o precedente em produção já faz assim
     (`InclusaoOcorrenciaDetalhes.vue:102`).

  Estilo: bootstrap-vue 2.18 / Bootstrap 4.6 puro, como as duas telas alvo.
  Nenhum import de `ant-design-vue` nem de `src/shared/components/`.
-->
<template>
  <div v-if="estado" class="ocorrencia-integracao-erro mt-1">
    <strong class="d-block text-danger">{{ titulo }}</strong>

    <small class="d-block text-danger">{{ corpo }}</small>

    <div v-if="acoesVisiveis.length > 0" class="mt-1">
      <template v-for="acao in acoesVisiveis">
        <!--
          Reenvio indisponível: a tela informa o porquê em vez de apenas omitir
          a ação — o bloqueio real é o 422 do endpoint.

          `tabindex`/`role`/`aria-*` portados de
          `InclusaoOcorrenciaDetalhes.vue:175-190`: o <span> inerte não é
          focável e sem isso o motivo só existiria no hover, que é exatamente o
          defeito que esta task combate. O `tabindex` também faz o tooltip abrir
          no foco, já que o trigger default da diretiva é `hover focus`.
        -->
        <span
          v-if="!acao.habilitada"
          :key="acao.chave"
          class="btn btn-sm btn-outline-secondary mr-1 mb-1 text-muted"
          style="cursor: not-allowed; opacity: 0.65"
          tabindex="0"
          role="button"
          aria-disabled="true"
          v-b-tooltip
          :title="tituloReenvioBloqueado"
          :aria-label="tituloReenvioBloqueado"
          >{{ acao.rotulo }}</span
        >
        <button
          v-else
          :key="acao.chave"
          type="button"
          class="btn btn-sm mr-1 mb-1"
          :class="acao.classe"
          @click="onAcao(acao)"
        >
          {{ acao.rotulo }}
        </button>
      </template>
    </div>

    <div v-if="temDetalhes" class="ocorrencia-integracao-erro-tecnico">
      <button
        type="button"
        class="btn btn-link btn-sm p-0"
        :aria-controls="collapseId"
        :aria-expanded="aberto ? 'true' : 'false'"
        @click="aberto = !aberto"
      >
        <i
          class="fa"
          :class="aberto ? 'fa-caret-down' : 'fa-caret-right'"
          aria-hidden="true"
        ></i>
        Detalhes técnicos
      </button>
      <b-collapse :id="collapseId" v-model="aberto">
        <dl class="row mb-0 small text-muted">
          <template v-for="(detalhe, indice) in detalhes">
            <dt :key="`${collapseId}-rotulo-${indice}`" class="col-5">
              {{ detalhe.rotulo }}
            </dt>
            <dd :key="`${collapseId}-valor-${indice}`" class="col-7">
              {{ detalhe.valor }}
            </dd>
          </template>
        </dl>
      </b-collapse>
    </div>
  </div>
</template>

<script>
import {
  ACOES,
  acoesDoEstado,
  corpoDoEstado,
  detalhesTecnicos,
  isReenvioBloqueado,
  resolverEstadoIntegracao,
  tituloDoEstado,
  tituloReenvioIndisponivel,
} from "../utils/ocorrenciaIntegracaoEstado";

export default {
  name: "OcorrenciaIntegracaoErro",

  props: {
    /** Ocorrência serializada por `CargaOcorrenciaResource` — camelCase. */
    item: {
      type: Object,
      required: true,
    },
    /**
     * SOMENTE permissão de reenvio, JÁ RESOLVIDA PELA VIEW (`getPermissionFw`).
     * Este componente não consulta permissão: `getPermissionFw` é controle de
     * interface, e a autoridade continua sendo o 422 do endpoint.
     *
     * Quem decide se a ação sai ATIVA ou INERTE é `isReenvioBloqueado(item)`,
     * logo abaixo. Somar as duas coisas nesta prop faz a ação desaparecer em vez
     * de sair inerte, e leva embora o tooltip que explica o bloqueio — foi
     * exatamente o defeito corrigido na TMS-8396 FE-04. Quem passar esta prop
     * passa permissão, nunca `reenvioPermitido`.
     *
     * O default é `true` porque esconder o botão de quem tem a permissão seria
     * entregar menos do que a tela já oferece hoje.
     */
    podeReenviar: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      /** Um booleano por instância — sem array na view, sem id global. */
      aberto: false,
    };
  },

  computed: {
    estado() {
      return resolverEstadoIntegracao(this.item);
    },

    titulo() {
      return tituloDoEstado(this.estado);
    },

    corpo() {
      return corpoDoEstado(this.estado, this.item);
    },

    detalhes() {
      return detalhesTecnicos(this.estado, this.item);
    },

    /**
     * A tabela diz se o estado tem bloco recolhido; a lista diz se sobrou
     * alguma linha. Os dois precisam concordar: `detalhe()` descarta valor
     * ausente, e um "Detalhes técnicos" que abre vazio é pior que não existir.
     */
    temDetalhes() {
      return (
        acoesDoEstado(this.estado).detalhesTecnicos && this.detalhes.length > 0
      );
    },

    tituloReenvioBloqueado() {
      return tituloReenvioIndisponivel(this.item);
    },

    /**
     * Ações renderizáveis, na ordem primária → secundária.
     *
     * A forma vem da tabela do helper; o botão de reenvio ainda atravessa dois
     * filtros que a tabela não conhece:
     *   - `podeReenviar === false` remove a ação inteira, como o precedente faz
     *     ao envolver link e <span> inerte no mesmo `getPermissionFw`;
     *   - `isReenvioBloqueado(item)` a mantém visível, porém inerte e com o
     *     tooltip que explica o porquê (guarda fail-open: `=== false`, nunca
     *     `!item.reenvioPermitido`).
     */
    acoesVisiveis() {
      const forma = acoesDoEstado(this.estado);
      const bloqueado = isReenvioBloqueado(this.item);

      return [
        { descritor: forma.primaria, classe: "btn-primary" },
        { descritor: forma.secundaria, classe: "btn-outline-secondary" },
      ]
        .filter((posicao) => posicao.descritor !== null)
        .filter(
          (posicao) =>
            posicao.descritor.chave !== ACOES.REENVIAR || this.podeReenviar
        )
        .map((posicao) => ({
          chave: posicao.descritor.chave,
          rotulo: posicao.descritor.rotulo,
          classe: posicao.classe,
          habilitada:
            posicao.descritor.habilitada &&
            (posicao.descritor.chave !== ACOES.REENVIAR || !bloqueado),
        }));
    },

    /**
     * `b-collapse` exige `id`, e a grade renderiza uma instância por linha. O id
     * sai de `carocorrenId`; `_uid` é só a rede de segurança para ocorrência sem
     * id, que não deve existir mas não pode derrubar a tela.
     */
    collapseId() {
      const id =
        this.item && this.item.carocorrenId != null
          ? this.item.carocorrenId
          : this._uid;

      return `ocorrencia-integracao-detalhes-${id}`;
    },
  },

  methods: {
    onAcao(acao) {
      if (acao.chave === ACOES.REENVIAR) {
        this.$emit("reenviar", this.item.carocorrenId);
        return;
      }

      this.$emit("copiar-dados-chamado", this.item);
    },
  },
};
</script>
